import { CSSProperties } from 'react';
import { ArdoqLogoLoaderIcon, IconSize } from '@ardoq/icons';
import { ArdoqLoaderSpinner } from './ArdoqLoaderSpinner';
import { ArdoqLoaderArgs, ArdoqLoaderComponentArgs } from './types';
import {
  ARDOQ_LOADER_COLORS,
  LoaderSize,
  LOADER_LABEL_TEXT,
  LOADER_ARIA_LABEL,
} from './consts';
import styled from 'styled-components';
import { colors, s16 } from '@ardoq/design-tokens';

const ArdoqLoaderContainer = styled.div`
  display: flex;
  gap: 12px;
`;

export const ARDOQ_LOADER_COMPONENT_SELECTOR = ArdoqLoaderContainer.toString();

const LoaderLabelContainer = styled.div`
  font-weight: 600;
  color: ${colors.textModerate};
`;

type LoaderComponentArgs = ArdoqLoaderComponentArgs & { isSpinner: boolean };

const toIconSize = (size: LoaderSize) => {
  return size as number as IconSize;
};

const LoaderComponent = ({ size, isSpinner, color }: LoaderComponentArgs) =>
  isSpinner ? (
    <ArdoqLoaderSpinner size={size} color={color} />
  ) : (
    <ArdoqLogoLoaderIcon iconSize={toIconSize(size)} color={color} />
  );

export const ArdoqLoaderComponent = ({
  labelPosition = 'vertical',
  loaderType = 'logo',
  loaderText = LOADER_LABEL_TEXT,
  size,
  loaderColor,
  hideSpinner,
  ariaLabel = LOADER_ARIA_LABEL,
}: ArdoqLoaderArgs) => {
  const color = ARDOQ_LOADER_COLORS[loaderColor];
  const isSpinner = loaderType === 'spinner';

  const isHorizontalLabel = labelPosition === 'horizontal';
  const containerStyle: CSSProperties = {
    fontSize: size === LoaderSize.SMALL ? s16 : '20px',
    alignItems: isHorizontalLabel ? 'flex-end' : 'center',
    flexDirection: isHorizontalLabel ? 'row' : 'column',
  };

  return (
    <ArdoqLoaderContainer
      style={containerStyle}
      role="status"
      aria-label={ariaLabel}
    >
      {!hideSpinner && (
        <LoaderComponent color={color} size={size} isSpinner={isSpinner} />
      )}
      {loaderText && (
        <LoaderLabelContainer
          style={{
            textAlign: isSpinner ? 'center' : undefined,
            lineHeight: size === LoaderSize.SMALL ? '18px' : '24px',
          }}
        >
          {loaderText}
        </LoaderLabelContainer>
      )}
    </ArdoqLoaderContainer>
  );
};
